/**
 * KontaktePage — Zentrale Kontaktdatenbank (HubSpot-Style)
 * Features: Serverseitiges Cursor-Paging (Issue #641), Filter-Sidebar + KPI-Header (Issue #640)
 *
 * Labels: Werden server-seitig via api/kontakt-liste aus der kontakt_labels Tabelle eingebettet.
 * Client rendert item.labels direkt ohne zusätzliche Supabase-Abfrage.
 */

import { useKontaktListe } from '@/hooks/useKontaktListe';
import { MEINE_FIRMEN, KONTAKT_TYPEN } from '@/lib/meine-firmen';
import { useCRMFirmen } from '@/hooks/useCRM';
import { BETRIEBE, type BetriebId } from '@/lib/betriebe';
import { KONTAKT_LABEL_COLORS, type KontaktLabel, ALL_KONTAKT_LABELS } from '@/lib/kontakt-labels';
import {
  matchesKontaktFilter,
  parseKontaktFilterFromSearch,
  serializeKontaktFilter,
  countActiveKontaktFilters,
  EMPTY_KONTAKT_FILTER,
  type KontaktFilterState,
  type KontaktMatchContext,
} from '@/lib/kontakt-filter-url';
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { supabase } from '@/lib/supabase';
import {
  Users, Plus, Search, Phone, Mail, MapPin, Building2,
  Star, Filter, Smartphone, X,
} from 'lucide-react';
import NeuerKontaktDialog from '@/components/NeuerKontaktDialog';
import { useCreateKontakt } from '@/hooks/useCRM';

const ROW_HEIGHT = 130;

interface KontaktKpi {
  total: number;
  auftraggeber: number;
  ohne_email: number;
  mit_verknuepfungen: number;
}

function useKontaktKpi() {
  const [kpi, setKpi] = useState<KontaktKpi | null>(null);
  useEffect(() => {
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const headers: HeadersInit = {};
        if (session?.access_token) headers['Authorization'] = `Bearer ${session.access_token}`;
        const res = await fetch('/api/kontakt-kpi', { headers });
        if (res.ok) setKpi(await res.json());
      } catch { /* non-critical */ }
    })();
  }, []);
  return kpi;
}

export default function KontaktePage() {
  const { create: createKontakt } = useCreateKontakt();
  const { firmen } = useCRMFirmen();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [kontakttyp, setKontakttyp] = useState('');
  const [firmaZuordnung, setFirmaZuordnung] = useState('');

  const [filter, setFilter] = useState<KontaktFilterState>(() =>
    parseKontaktFilterFromSearch(window.location.search)
  );

  const kpi = useKontaktKpi();

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // Persist filter state to URL
  useEffect(() => {
    const params = serializeKontaktFilter(filter);
    const qs = params.toString();
    const newUrl = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
    window.history.replaceState(null, '', newUrl);
  }, [filter]);

  // Restore filter on browser back/forward
  useEffect(() => {
    const onPopState = () => setFilter(parseKontaktFilterFromSearch(window.location.search));
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const { items, total, loading, loadingMore, hasMore, loadMore } = useKontaktListe({
    search: debouncedSearch || undefined,
    kategorie: kontakttyp || undefined,
    firma_zuordnung: firmaZuordnung || undefined,
  });

  // Client-side filtering on top of server-paged items
  const filteredItems = useMemo(() => {
    const result = items.filter((item) =>
      matchesKontaktFilter(
        {
          email: item.email,
          telefon: item.telefon,
          mobil: item.mobil,
          adresse: item.adresse,
          plz: item.plz,
          labels: item.labels as KontaktLabel[],
          betriebe: item.tags,
          quelle: item.quelle,
        } as KontaktMatchContext,
        filter
      )
    );
    if (filter.sortBy === 'firma') {
      result.sort((a, b) => (a.firma ?? '').localeCompare(b.firma ?? '', 'de'));
    } else if (filter.sortBy === 'letzte_aktivitaet') {
      result.sort((a, b) => (b.letzte_aktivitaet ?? '').localeCompare(a.letzte_aktivitaet ?? ''));
    } else if (filter.sortBy === 'umsatz_gesamt') {
      result.sort((a, b) => (b.umsatz_gesamt ?? 0) - (a.umsatz_gesamt ?? 0));
    }
    return result;
  }, [items, filter]);

  const activeFilterCount = countActiveKontaktFilters(filter);

  // Virtual scroll
  const parentRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: filteredItems.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 5,
  });

  // Infinite scroll via IntersectionObserver
  const sentinelRef = useRef<HTMLDivElement>(null);

  const handleIntersect = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      if (entries[0]?.isIntersecting && hasMore && !loadingMore) loadMore();
    },
    [hasMore, loadingMore, loadMore]
  );

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(handleIntersect, {
      root: parentRef.current,
      threshold: 0.1,
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [handleIntersect]);

  const virtualItems = virtualizer.getVirtualItems();

  function toggleLabel(label: KontaktLabel) {
    setFilter((f) => ({
      ...f,
      labels: f.labels.includes(label) ? f.labels.filter((l) => l !== label) : [...f.labels, label],
    }));
  }

  return (
    <div className="p-4 lg:p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
            <Users className="w-5 h-5 text-gray-500" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-bold text-foreground">Kontakte</h1>
              {total > 0 && (
                <span className="text-[10px] font-medium text-gray-400">
                  {total.toLocaleString('de')} Kontakte
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground">Zentrale Kontaktdatenbank aller Betriebe</p>
          </div>
        </div>
        <button
          onClick={() => setShowNewDialog(true)}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold text-white bg-[#1B3A5C] hover:bg-[#152d49] transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span className="hidden sm:inline">Neuer Kontakt</span>
        </button>
      </div>

      {/* KPI Header */}
      {kpi && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-white rounded-xl border border-gray-200 p-3">
            <p className="text-[11px] text-gray-500 mb-1">Gesamt</p>
            <p className="text-xl font-bold text-foreground" data-testid="kpi-total">
              {kpi.total.toLocaleString('de')}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-3">
            <p className="text-[11px] text-gray-500 mb-1">Auftraggeber</p>
            <p className="text-xl font-bold text-[#1E40AF]" data-testid="kpi-auftraggeber">
              {kpi.auftraggeber.toLocaleString('de')}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-3">
            <p className="text-[11px] text-gray-500 mb-1">Ohne E-Mail</p>
            <p className="text-xl font-bold text-amber-600" data-testid="kpi-ohne-email">
              {kpi.ohne_email.toLocaleString('de')}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-3">
            <p className="text-[11px] text-gray-500 mb-1">Mit Verknüpfungen</p>
            <p className="text-xl font-bold text-green-600" data-testid="kpi-mit-verknuepfungen">
              {kpi.mit_verknuepfungen.toLocaleString('de')}
            </p>
          </div>
        </div>
      )}

      {/* Search + Filter Bar */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Name, Firma oder E-Mail suchen..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1B3A5C]/20"
          />
        </div>
        <select
          value={firmaZuordnung}
          onChange={(e) => setFirmaZuordnung(e.target.value)}
          data-testid="filter-meine-firma"
          className="px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#1B3A5C]/20"
          title="Meine Firma"
        >
          <option value="">Alle Firmen</option>
          {MEINE_FIRMEN.map((f) => (
            <option key={f.value} value={f.value}>{f.label}</option>
          ))}
        </select>
        <select
          value={kontakttyp}
          onChange={(e) => setKontakttyp(e.target.value)}
          data-testid="filter-kontakttyp"
          className="px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#1B3A5C]/20"
          title="Kontakttyp"
        >
          <option value="">Alle Typen</option>
          {KONTAKT_TYPEN.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`relative px-3 py-2.5 rounded-xl border text-sm transition-colors flex items-center gap-1.5
            ${showFilters || activeFilterCount > 0 ? 'border-[#1B3A5C] bg-[#1B3A5C]/5 text-[#1B3A5C]' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}
        >
          <Filter className="w-4 h-4" />
          <span className="hidden sm:inline">Filter</span>
          {activeFilterCount > 0 && (
            <span
              data-testid="filter-badge"
              className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-[#1B3A5C] text-white text-[9px] font-bold flex items-center justify-center"
            >
              {activeFilterCount}
            </span>
          )}
        </button>
        {activeFilterCount > 0 && (
          <button
            onClick={() => setFilter(EMPTY_KONTAKT_FILTER)}
            className="px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-500 hover:border-gray-300 flex items-center gap-1"
            title="Alle Filter zurücksetzen"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Filter Sidebar */}
      {showFilters && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
          {/* Labels */}
          <div data-testid="filter-labels">
            <p className="text-xs font-semibold text-gray-500 mb-2">Labels</p>
            <div className="flex flex-wrap gap-1.5">
              {ALL_KONTAKT_LABELS.map((label) => {
                const cfg = KONTAKT_LABEL_COLORS[label];
                const active = filter.labels.includes(label);
                return (
                  <button
                    key={label}
                    onClick={() => toggleLabel(label)}
                    className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors border
                      ${active ? 'border-[#1B3A5C]' : 'border-transparent bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                    style={active ? { backgroundColor: cfg.bg, color: cfg.text } : undefined}
                  >
                    {cfg.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Betrieb */}
          <div data-testid="filter-betrieb">
            <p className="text-xs font-semibold text-gray-500 mb-2">Betrieb</p>
            <div className="flex flex-wrap gap-1.5">
              <button
                onClick={() => setFilter((f) => ({ ...f, betrieb: '' }))}
                className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors
                  ${!filter.betrieb ? 'bg-[#1B3A5C] text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
              >
                Alle
              </button>
              {Object.entries(BETRIEBE).map(([id, b]) => (
                <button
                  key={id}
                  onClick={() => setFilter((f) => ({ ...f, betrieb: f.betrieb === id ? '' : id }))}
                  className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors
                    ${filter.betrieb === id ? 'bg-[#1B3A5C] text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                >
                  {b.name}
                </button>
              ))}
            </div>
          </div>

          {/* PLZ */}
          <div data-testid="filter-plz">
            <p className="text-xs font-semibold text-gray-500 mb-2">PLZ (Präfix)</p>
            <input
              type="text"
              placeholder="z.B. 586"
              value={filter.plz}
              onChange={(e) => setFilter((f) => ({ ...f, plz: e.target.value }))}
              className="w-32 px-3 py-1.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B3A5C]/20"
              maxLength={5}
            />
          </div>

          {/* Checkbox filters */}
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={filter.hatEmail}
                onChange={(e) => setFilter((f) => ({ ...f, hatEmail: e.target.checked }))}
                className="w-3.5 h-3.5"
                data-testid="filter-hat-email"
              />
              Hat E-Mail
            </label>
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={filter.hatTelefon}
                onChange={(e) => setFilter((f) => ({ ...f, hatTelefon: e.target.checked }))}
                className="w-3.5 h-3.5"
                data-testid="filter-hat-telefon"
              />
              Hat Telefon
            </label>
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={filter.hatAdresse}
                onChange={(e) => setFilter((f) => ({ ...f, hatAdresse: e.target.checked }))}
                className="w-3.5 h-3.5"
                data-testid="filter-hat-adresse"
              />
              Hat Adresse
            </label>
          </div>

          {/* Importquelle */}
          <div data-testid="filter-importquelle">
            <p className="text-xs font-semibold text-gray-500 mb-2">Importquelle</p>
            <div className="flex flex-wrap gap-1.5">
              {['', 'import_csv', 'winvalue', 'rentsoft', 'manuell'].map((q) => (
                <button
                  key={q || 'alle'}
                  onClick={() => setFilter((f) => ({ ...f, importquelle: q }))}
                  className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors
                    ${filter.importquelle === q ? 'bg-[#1B3A5C] text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                >
                  {q || 'Alle'}
                </button>
              ))}
            </div>
          </div>

          {/* Sortierung */}
          <div data-testid="filter-sortierung">
            <p className="text-xs font-semibold text-gray-500 mb-2">Sortierung</p>
            <div className="flex flex-wrap gap-1.5">
              {(['name', 'firma', 'letzte_aktivitaet', 'umsatz_gesamt'] as const).map((s) => (
                <button
                  key={s}
                  onClick={() => setFilter((f) => ({ ...f, sortBy: s }))}
                  className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors
                    ${filter.sortBy === s ? 'bg-[#1B3A5C] text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                >
                  {s === 'name' ? 'Name' : s === 'firma' ? 'Firma' : s === 'letzte_aktivitaet' ? 'Letzte Aktivität' : 'Umsatz'}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Contacts List */}
      {loading ? (
        <div className="py-12 text-center text-sm text-muted-foreground">
          Kontakte werden geladen...
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Users className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">
            {debouncedSearch || activeFilterCount > 0 ? 'Keine Kontakte gefunden' : 'Noch keine Kontakte vorhanden'}
          </p>
          {!debouncedSearch && activeFilterCount === 0 && (
            <button
              onClick={() => setShowNewDialog(true)}
              className="mt-3 text-sm text-[#1B3A5C] font-medium hover:underline"
            >
              Ersten Kontakt erstellen
            </button>
          )}
        </div>
      ) : (
        <>
          <div
            ref={parentRef}
            className="kontakt-virtual-container overflow-y-auto"
            style={{ height: 'calc(100vh - 260px)', minHeight: 400 }}
          >
            <div
              style={{
                height: virtualizer.getTotalSize(),
                width: '100%',
                position: 'relative',
              }}
            >
              {virtualItems.map((virtualRow) => {
                const kontakt = filteredItems[virtualRow.index];
                if (!kontakt) return null;
                return (
                  <div
                    key={kontakt.id}
                    className="kontakt-row absolute left-0 right-0 px-0 py-1"
                    style={{ top: virtualRow.start, height: ROW_HEIGHT }}
                  >
                    <div className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-sm transition-shadow h-full overflow-hidden">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#1B3A5C]/10 flex items-center justify-center text-sm font-bold text-[#1B3A5C] flex-shrink-0">
                          {kontakt.name
                            .split(/[\s,]+/)
                            .filter(Boolean)
                            .map((n) => n[0])
                            .join('')
                            .slice(0, 2)
                            .toUpperCase()}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <h3
                              className="text-sm font-bold truncate text-foreground"
                              data-testid="kontakt-name"
                            >
                              {kontakt.name}
                            </h3>
                            {kontakt.favorit && (
                              <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400 flex-shrink-0" />
                            )}
                          </div>
                          {kontakt.firma && (
                            <div className="flex items-center gap-1 mt-0.5">
                              <Building2 className="w-3 h-3 text-gray-400" />
                              <span className="text-[11px] text-gray-400 truncate">{kontakt.firma}</span>
                            </div>
                          )}
                          <div className="mt-2 space-y-1">
                            {kontakt.email && (
                              <a
                                href={`mailto:${kontakt.email}`}
                                className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-[#1B3A5C] transition-colors"
                              >
                                <Mail className="w-3 h-3" />
                                <span className="truncate">{kontakt.email}</span>
                              </a>
                            )}
                            {kontakt.telefon && (
                              <a
                                href={`tel:${kontakt.telefon}`}
                                className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-[#1B3A5C] transition-colors"
                              >
                                <Phone className="w-3 h-3" />
                                <span>{kontakt.telefon}</span>
                              </a>
                            )}
                            {kontakt.mobil && (
                              <a
                                href={`tel:${kontakt.mobil}`}
                                className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-[#1B3A5C] transition-colors"
                              >
                                <Smartphone className="w-3 h-3" />
                                <span>{kontakt.mobil}</span>
                              </a>
                            )}
                            {kontakt.ort && (
                              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                                <MapPin className="w-3 h-3" />
                                <span>
                                  {kontakt.plz} {kontakt.ort}
                                </span>
                              </div>
                            )}
                          </div>
                          {kontakt.tags && kontakt.tags.length > 0 && (
                            <div className="flex items-center gap-1 mt-2 flex-wrap">
                              {kontakt.tags.slice(0, 4).map((tag: string) => {
                                const betrieb = BETRIEBE[tag as BetriebId];
                                return (
                                  <span
                                    key={tag}
                                    className="text-[9px] font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-500"
                                  >
                                    {betrieb ? betrieb.name : tag}
                                  </span>
                                );
                              })}
                              {kontakt.tags.length > 4 && (
                                <span className="text-[9px] text-gray-400">
                                  +{kontakt.tags.length - 4}
                                </span>
                              )}
                            </div>
                          )}
                          {kontakt.labels && kontakt.labels.length > 0 && (
                            <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                              {kontakt.labels.slice(0, 3).map((label: string) => {
                                const cfg = KONTAKT_LABEL_COLORS[label as KontaktLabel];
                                if (!cfg) return null;
                                return (
                                  <span
                                    key={label}
                                    className="text-[9px] font-medium px-1.5 py-0.5 rounded"
                                    style={{ backgroundColor: cfg.bg, color: cfg.text }}
                                  >
                                    {cfg.label}
                                  </span>
                                );
                              })}
                              {kontakt.labels.length > 3 && (
                                <span className="text-[9px] text-gray-400">
                                  +{kontakt.labels.length - 3} weitere
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Sentinel inside scrollable container for IntersectionObserver */}
            <div ref={sentinelRef} className="h-4" />
          </div>

          {loadingMore && (
            <div className="py-4 text-center text-xs text-muted-foreground">
              Weitere Kontakte werden geladen…
            </div>
          )}
        </>
      )}

      <NeuerKontaktDialog
        open={showNewDialog}
        onClose={() => setShowNewDialog(false)}
        onCreate={createKontakt}
        firmen={firmen}
      />
    </div>
  );
}
